import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Client } from '../db';
import { clientsAPI, salesAPI } from '../backend';
import { Edit, Trash2, Users, Plus, AlertTriangle } from 'lucide-react';
import { formatPhone } from '../utils/formatPhone';

function FollowUpIcon({ daysSinceLast }: { daysSinceLast: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({ top: rect.top - 8, left: rect.left + rect.width / 2 });
    }
  };

  return (
    <span
      ref={ref}
      className="client-followup-icon"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setPos(null)}
    >
      <AlertTriangle size={13} />
      {pos && createPortal(
        <span
          className="client-followup-tooltip"
          style={{ top: pos.top, left: pos.left }}
        >
          Sin compras en {daysSinceLast} días. Se recomienda hacer seguimiento.
        </span>,
        document.body
      )}
    </span>
  );
}

function Clients() {
  const [clients, setClients] = useState<Client[]>([]);
  const [salesByClient, setSalesByClient] = useState<Record<number, { count: number; lastDate: Date }>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<'list' | 'new' | 'edit'>('list');
  const [clientEdit, setClientEdit] = useState<Client | null>(null);
  const [form, setForm] = useState<Omit<Client, 'id'>>({ name: '', email: '', phone: '', address: '', type: 'Distribuidor' });
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'last_purchase' | 'total_purchases' | 'follow_up' | 'recently_added'>('name');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, salesData] = await Promise.all([clientsAPI.getAll(), salesAPI.getAll()]);
      setClients(data);

      const stats: Record<number, { count: number; lastDate: Date }> = {};
      salesData.forEach((s: any) => {
        const cid = s.client?.id ?? s.client_id;
        if (!cid) return;
        const date = new Date(s.created_at);
        if (!stats[cid]) {
          stats[cid] = { count: 1, lastDate: date };
        } else {
          stats[cid].count += 1;
          if (date > stats[cid].lastDate) stats[cid].lastDate = date;
        }
      });
      setSalesByClient(stats);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error loading clients');
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    setForm({ name: '', email: '', phone: '', address: '', type: 'Distribuidor' });
    setMode('new');
  };

  const handleEdit = (client: Client) => {
    setClientEdit(client);
    setForm({ name: client.name, email: client.email, phone: client.phone, address: client.address, type: client.type ?? 'Distribuidor'});
    setMode('edit');
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Seguro que deseas eliminar este cliente?')) {
      try {
        await clientsAPI.delete(id);
        await loadClients();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Error deleting client');
      }
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload = { ...form, phone: formatPhone(form.phone) };
      if (mode === 'new') {
        await clientsAPI.create(payload);
      } else if (mode === 'edit' && clientEdit) {
        await clientsAPI.update(clientEdit.id, payload);
      }
      await loadClients();
      setMode('list');
      setClientEdit(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error saving client');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Cargando clientes...</p>
      </div>
    );
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  const filteredClients = clients
    .filter(client =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.phone.includes(searchTerm)
    )
    .sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name, 'es');
        case 'last_purchase': {
          const aDate = salesByClient[a.id]?.lastDate?.getTime() ?? 0;
          const bDate = salesByClient[b.id]?.lastDate?.getTime() ?? 0;
          return bDate - aDate;
        }
        case 'total_purchases': {
          const aCount = salesByClient[a.id]?.count ?? 0;
          const bCount = salesByClient[b.id]?.count ?? 0;
          return bCount - aCount;
        }
        case 'follow_up': {
          const now = Date.now();
          const aDays = salesByClient[a.id] ? (now - salesByClient[a.id].lastDate.getTime()) / 86400000 : Infinity;
          const bDays = salesByClient[b.id] ? (now - salesByClient[b.id].lastDate.getTime()) / 86400000 : Infinity;
          return bDays - aDays;
        }
        case 'recently_added':
          return b.id - a.id;
        default:
          return 0;
      }
    });

  return (
    <div className="main-view">
      <div className="clients-header">
        <h1>Clientes</h1>
        <p>Gestiona tu base de datos de clientes</p>
      </div>

      {mode === 'list' && (
        <>
          <div className="search-section">
            <input
              type="text"
              placeholder="Buscar clientes por nombre o correo..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as typeof sortBy)}
              className="status-filter"
            >
              <option value="name">Nombre (A-Z)</option>
              <option value="recently_added">Recién agregados</option>
              <option value="last_purchase">Última compra</option>
              <option value="total_purchases">Más compras</option>
              <option value="follow_up">Necesitan seguimiento</option>
            </select>
            <button
              className="btn-primary"
              onClick={handleNew}
            >
              <Plus size={16} />
              <span>Nuevo Cliente</span>
            </button>
          </div>

          <div className="clients-stats">
            <div className="stat-item">
              <div className="stat-value">{clients.length}</div>
              <div className="stat-label">Total Clientes</div>
            </div>
          </div>

          {filteredClients.length > 0 ? (
            <div className="clients-grid">
              {filteredClients.map(client => {
                const stats = salesByClient[client.id];
                const now = new Date();
                const daysSinceLast = stats
                  ? Math.floor((now.getTime() - stats.lastDate.getTime()) / (1000 * 60 * 60 * 24))
                  : null;
                const needsFollowUp = daysSinceLast !== null && daysSinceLast > 30;

                return (
                <div key={client.id} className="client-card">
                  <div className="client-avatar">
                    {client.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="client-info">
                    <div className="client-name-row">
                      <h3 className="client-item name">{client.name}</h3>
                    </div>
                    <p className="client-item">{client.email}</p>
                    <p className="client-item">{formatPhone(client.phone)}</p>
                    <p className="client-item">{client.address}</p>
                    <div className="client-purchase-stats">
                      {stats ? (
                        <>
                          <span>{stats.count} {stats.count === 1 ? 'compra' : 'compras'}</span>
                          <span className="client-stat-dot">·</span>
                          <span>Última: {stats.lastDate.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                          {needsFollowUp && <FollowUpIcon daysSinceLast={daysSinceLast!} />}
                        </>
                      ) : (
                        <span className="client-no-purchases">Sin compras</span>
                      )}
                    </div>
                  </div>
                  <div className="client-actions">
                    <button
                      className="btn-secondary btn-small"
                      onClick={() => handleEdit(client)}
                      title="Editar cliente"
                    >
                      <Edit size={16} />
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="no-clients">
              <div className="no-clients-icon">
                <Users size={48} />
              </div>
              <h3>No hay clientes</h3>
              <p>
                {searchTerm 
                  ? 'No se encontraron clientes con esa búsqueda.' 
                  : 'Comienza agregando tu primer cliente.'
                }
              </p>
              {!searchTerm && (
                <button className="btn-primary" onClick={handleNew}>
                  <Plus size={16} />
                  <span>Agregar Cliente</span>
                </button>
              )}
            </div>
          )}
        </>
      )}

      {(mode === 'new' || mode === 'edit') && (
        <div className="client-form-container">
          <div className="client-form-card">
            <div className="form-header">
              <h2>{mode === 'new' ? 'Nuevo Cliente' : 'Editar Cliente'}</h2>
            </div>
            
            <form className="client-form" onSubmit={handleSave}>
              <div className="form-group">
                <label htmlFor="name" className="required">Nombre completo</label>
                <input
                  id="name"
                  type="text"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Ingresa el nombre completo"
                  required
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="email">Correo electrónico</label>
                <input
                  id="email"
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="ejemplo@email.com"
                  className="form-input"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="phone">Teléfono</label>
                <input
                  id="phone"
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  placeholder="+1 234-567-8900"
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="address" className="required">Dirección</label>
                <input
                  id="address"
                  type="text"
                  value={form.address}
                  onChange={e => setForm(f => ({ ...f, address: e.target.value }))}
                  placeholder="Calle 123, Ciudad"
                  required
                  className="form-input"
                />
              </div>

              <div className="form-group">
                <label htmlFor="type" className="required">Tipo</label>
                <select
                  id="type"
                  value={form.type}
                  onChange={e => setForm(f => ({ ...f, type: e.target.value as 'Distribuidor' | 'Persona' }))}
                  className="form-input"
                  required
                >
                  <option value="Distribuidor">Distribuidor</option>
                  <option value="Persona">Persona</option>
                </select>
              </div>
              
              <div className="form-actions">
                <button className="btn-secondary" type="button" onClick={() => { setMode('list'); setClientEdit(null); }}>
                  Cancelar
                </button>
                {mode === 'edit' && clientEdit && (
                  <button
                    className="btn-secondary btn-danger"
                    type="button"
                    onClick={() => handleDelete(clientEdit.id)}
                  >
                    <Trash2 size={16} />
                    Eliminar
                  </button>
                )}
                <button className="btn-primary" type="submit">
                  {mode === 'new' ? 'Crear Cliente' : 'Guardar Cambios'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Clients; 